// @flow
/** @jsx isolateComponent */

import { Fragment } from 'react';
import { type HOC, pure } from 'recompose';

import { Button } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';

import FilterGroup from 'app/beta-editor/components/formatic/FilterGroup';
import FilterGroupsStyles from 'app/beta-editor/components/formatic/FilterGroupsStyles';
import FilterRuleDivider from 'app/beta-editor/components/formatic/FilterRuleDivider';
import RuleUtils from 'app/beta-editor/utils/RuleUtils';
import Tag from 'app/common/components/Tag';
import maybeRender from 'app/common/components/maybeRender';
import { interpose } from 'app/common/CommonUtils';
import { isPathNode } from 'app/beta-editor/utils/PathUtils';

const runStatusToKind = {
  true: 'success',
  false: 'failure',
};

const runStatusToMatchText = {
  true: 'MATCH',
  false: 'NO MATCH',
};

const runStatusToMatchTextForPaths = {
  true: 'Path Would Run',
  false: `Path Wouldn't Run`,
};

const MatchTag = ({ groupWouldRun, isPath }) => (
  <Tag
    css={FilterGroupsStyles.wouldRunTag}
    kind={runStatusToKind[groupWouldRun]}
  >
    {isPath
      ? runStatusToMatchTextForPaths[groupWouldRun]
      : runStatusToMatchText[groupWouldRun]}
  </Tag>
);

const MaybeRenderMatchTag = maybeRender(props => props.shouldShowTestResults)(
  MatchTag
);

type FilterGroupsProps = {
  config: Object,
  field: Object,
  groups: Array<Object>,
  onAction: Function,
  onAddGroup: Function,
  onAddRule: Function,
  onChangeRule: Function,
  onDeleteRule: Function,
};

const FilterGroups = (props: FilterGroupsProps) => {
  const { flow, node } = props.field.meta;
  const { filters } = RuleUtils.filtersResult(flow, node.id);
  const filterResultsByGroup = RuleUtils.resultByGroup(filters);
  const groups = (props.groups || []).map((group, i) => {
    const groupTestResults = filterResultsByGroup[group.groupId];
    const groupWouldRun = RuleUtils.groupWouldRun(groupTestResults);
    const isLastGroup = i === props.groups.length - 1;
    return (
      <div key={group.groupId}>
        <MaybeRenderMatchTag
          groupWouldRun={groupWouldRun}
          isPath={isPathNode(node)}
        />
        <FilterGroup
          config={props.config}
          field={props.field}
          groupId={group.groupId}
          isLastGroup={isLastGroup}
          onAction={props.onAction}
          onAddGroup={props.onAddGroup}
          onAddRule={props.onAddRule}
          onChangeRule={props.onChangeRule}
          onDeleteRule={props.onDeleteRule}
          ruleResultsById={RuleUtils.resultsById(filters)}
          rules={group.rules}
        />
        {isLastGroup ? null : <hr css={FilterGroupsStyles.groupDividerLine} />}
      </div>
    );
  });
  const nodes = interpose(groups, group => (
    <FilterRuleDivider key={`${group.key}-divider`} word="or" />
  ));
  return (
    <Fragment>
      {nodes}
      {nodes.length !== 0 ? null : (
        <div css={FilterGroupsStyles.addFilterButton}>
          <Button
            color="secondary"
            isMinWidth={true}
            isSlim={true}
            onClick={props.onAddGroup}
            size="small"
          >
            + ADD FILTER
          </Button>
        </div>
      )}
    </Fragment>
  );
};

const enhanceFilterGroups: HOC<*, FilterGroupsProps> = pure;

const EnhancedFilterGroups = enhanceFilterGroups(FilterGroups);

export default EnhancedFilterGroups;
